/**
 * Notify — public subscribe form (notify/spec.md §1). Route `/notify/subscribe` (standalone) and
 * `/notify/subscribe?embed=1` (widget iframe variant, ADR-0004). Email + name, PLUS the postal-
 * updates.md delivery-preference extension: a resident may choose Email / Mail / Both, and Mail/
 * Both show a structured mailing address (MailingAddressFields). Email stays required only when the
 * method needs it (email/both) — mail-only relabels the email field "(optional)" and requires the
 * address instead (postal-updates.md scope §1-§3).
 *
 * No "I agree" checkbox opt-in (spec §1.4): submitting with an email IS the opt-in action; the
 * confirmation email's click is the actual double-opt-in event. Consent microcopy is static text,
 * wired to the email field via aria-describedby so screen readers hear it as part of the field's
 * description. The CHANNEL checkbox group added below (stay-informed-channels.md §3.1) is a
 * DIFFERENT thing — it's a preference picker ("which updates"), not a consent checkbox, and an
 * empty selection is valid (means "everything"), so it does not change spec §1.4's rule.
 *
 * Success state replaces the form in place (not a redirect). For email/both it's IDENTICAL for a
 * fresh signup and an already-pending/confirmed email (spec §1.6 anti-enumeration) — the server-side
 * status branch never reaches the UI as a different shape. Mail-only is single-opt-in (postal-
 * updates.md — CAN-SPAM governs email, not postal mail) so it gets its own "confirmed immediately,
 * no email" success copy, with no "check your email" language or resend button.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { subscribe, resendConfirmation, ApiError } from "../../api/index.js";
import type { DeliveryMethod, MailingAddress } from "../../api/types.js";
import { Field } from "../../components/Field.js";
import { Button } from "../../components/Button.js";
import { CaptchaField } from "../../components/CaptchaField.js";
import { CheckIcon, SpinnerIcon } from "../../components/icons.js";
import { TownHeader } from "../../components/TownHeader.js";
import { ResidentThemeRoot } from "../../components/ResidentThemeRoot.js";
import { useTheme } from "../../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../../components/useDocumentTitle.js";
import { useEmbedClose } from "../../components/useEmbedClose.js";
import { CAPTCHA_ENABLED } from "../../api/wafCaptcha.js";
import { useCaptcha } from "../../components/useCaptcha.js";
import { DeliveryMethodField } from "../../components/DeliveryMethodField.js";
import {
  MailingAddressFields,
  emptyMailingAddress,
  validateMailingAddress,
  type MailingAddressErrors,
} from "../../components/MailingAddressFields.js";

const ID = {
  email: "field-notify-email",
  name: "field-notify-name",
  captcha: "field-notify-captcha",
  channelsLegend: "field-notify-channels-legend",
};

interface Props {
  embed?: boolean;
}

type SubmitState = "idle" | "submitting" | "error";
type ResendState = "idle" | "sending" | "sent" | "rate-limited";
/** What the success screen renders (postal-updates.md "Success screen: THREE variants"). */
type SuccessKind = "pending" | "mail-only";

export function SubscribePage({ embed = false }: Props): JSX.Element {
  const { config, channels } = useTheme();
  useDocumentTitle(pageTitle("Get City Updates", config.townName));
  useEmbedClose(embed);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("email");
  const [mailingAddress, setMailingAddress] = useState<MailingAddress>(emptyMailingAddress());
  const [addressErrors, setAddressErrors] = useState<MailingAddressErrors>({});
  const captcha = useCaptcha();
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [subscribedEmail, setSubscribedEmail] = useState<string | null>(null);
  const [successKind, setSuccessKind] = useState<SuccessKind>("pending");
  const [resendState, setResendState] = useState<ResendState>("idle");
  const headingRef = useRef<HTMLHeadingElement>(null);

  const needsEmail = deliveryMethod === "email" || deliveryMethod === "both";
  const needsAddress = deliveryMethod === "mail" || deliveryMethod === "both";

  // "What updates do you want?" channel checkboxes (stay-informed-channels.md §3.1) — DefaultOn
  // channels start checked; an empty selection is a valid submission (means "everything", resolved
  // server-side, never expanded client-side). Seeded once the live channel list arrives.
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(
    () => new Set(channels.filter((c) => c.defaultOn).map((c) => c.id)),
  );
  const seededDefaultsRef = useRef(false);
  useEffect(() => {
    if (seededDefaultsRef.current) return;
    if (!channels.length) return;
    seededDefaultsRef.current = true;
    setSelectedChannels(new Set(channels.filter((c) => c.defaultOn).map((c) => c.id)));
  }, [channels]);

  const toggleChannel = (id: string) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (submitted) headingRef.current?.focus();
  }, [submitted]);

  const requiredMissing =
    (needsEmail && !isValidEmail(email)) ||
    // Name is the envelope recipient — required whenever we're mailing (mail/both).
    (needsAddress && !name.trim()) ||
    (needsAddress && Object.keys(validateMailingAddress(mailingAddress)).length > 0) ||
    // The captcha is now solved INLINE before submit (live) or via the fallback checkbox (dev/test),
    // so the button reflects captcha-ready in both modes.
    !captcha.passed;

  // "In flight" = the POST. Re-entry is blocked so a second click can't fire a duplicate subscribe.
  const busy = submitState === "submitting";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;

    const trimmed = email.trim();
    let hasError = false;
    if (needsEmail && !isValidEmail(trimmed)) {
      setEmailError("Enter a valid email address, like name@example.com.");
      hasError = true;
    } else {
      setEmailError(null);
    }
    if (needsAddress && !name.trim()) {
      setNameError("Enter a name so we know who to address the mail to.");
      hasError = true;
    } else {
      setNameError(null);
    }
    if (needsAddress) {
      const errs = validateMailingAddress(mailingAddress);
      setAddressErrors(errs);
      if (Object.keys(errs).length > 0) hasError = true;
    } else {
      setAddressErrors({});
    }
    if (hasError) return;
    if (!CAPTCHA_ENABLED && !captcha.passed) {
      setSubmitErrorMsg(null);
      setSubmitState("error");
      return;
    }
    // Submit safety net: the puzzle was solved inline on mount, so this is a fast no-op when that
    // token is still fresh; if it expired (WAF tokens are short-lived) it re-challenges before the
    // POST. The solved token rides the POST as the `x-aws-waf-token` header (apiFetch — cookie-
    // independent, works in the cross-site embed). No-op when the live WAF is disabled.
    if (!(await captcha.ensureForSubmit())) return;
    setSubmitState("submitting");
    setSubmitErrorMsg(null);
    try {
      // Response status is deliberately ignored — anti-enumeration means the email
      // path renders the same success screen regardless of what the server returns.
      await subscribe(
        needsEmail ? trimmed : undefined,
        name.trim() || undefined,
        [...selectedChannels],
        deliveryMethod,
        needsAddress
          ? {
              ...mailingAddress,
              state: mailingAddress.state.trim().toUpperCase(),
              line2: mailingAddress.line2?.trim() || undefined,
            }
          : undefined,
      );
      setSubscribedEmail(needsEmail ? trimmed : null);
      // Anti-enumeration: an email signup ALWAYS shows the same "check your email"
      // screen, whether the address is new, pending, or already confirmed — the
      // server now returns an invariant {status:"pending"} for all of them, and the
      // UI never branches on membership. Only the single-opt-in mail path differs.
      setSuccessKind(deliveryMethod === "mail" ? "mail-only" : "pending");
      setSubmitted(true);
      setSubmitState("idle");
    } catch (err) {
      setSubmitState("error");
      setSubmitErrorMsg(
        err instanceof ApiError ? err.message : "We couldn't submit that — try again.",
      );
    }
  };

  const onResend = async () => {
    if (!subscribedEmail || resendState === "sending") return;
    setResendState("sending");
    try {
      await resendConfirmation(subscribedEmail);
      setResendState("sent");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setResendState("rate-limited");
      else setResendState("idle");
    }
  };

  if (submitted) {
    const title =
      successKind === "mail-only"
        ? "You're signed up for mailed updates."
        : "Almost done — check your email.";
    return (
      <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
        <TownHeader embed={embed} />
        <main className="td-confirm">
          <p className="td-sr-only" aria-live="polite">
            {successKind === "mail-only"
              ? `Signed up for mailed updates from ${config.townName}.`
              : "Subscribed. Check your email to confirm."}
          </p>
          <div className="td-confirm__mark" aria-hidden="true">
            <CheckIcon width={28} height={28} />
          </div>
          <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
            {title}
          </h1>
          {successKind === "mail-only" ? (
            <p className="td-confirm__sub">
              You're signed up for mailed updates from {config.townName}. We'll send notices to the
              mailing address you gave us — no further action needed.
            </p>
          ) : (
            <>
              <p className="td-confirm__sub">
                We sent a confirmation link to <strong>{subscribedEmail}</strong>. Click it to start
                getting updates from {config.townName}.
              </p>
              <p className="td-confirm__sub">
                Didn't get it? Check spam, or resend the email below.
              </p>
              <div className="td-confirm__actions">
                <Button variant="secondary" onClick={onResend} disabled={resendState === "sending"}>
                  {resendState === "sending" ? "Sending…" : "Resend confirmation email"}
                </Button>
              </div>
              <p aria-live="polite" className="td-submit__hint">
                {resendState === "sent" ? "Sent — check your inbox." : null}
                {resendState === "rate-limited"
                  ? "Please wait a minute before requesting another."
                  : null}
              </p>
            </>
          )}
          <p className="td-confirm__sub">
            Already subscribed? <Link to="/notify/manage">Manage your updates →</Link>
          </p>
        </main>
        <footer className="td-footer">
          <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
          {config.privacyUrl ? (
            <a href={config.privacyUrl} rel="noopener">
              Privacy
            </a>
          ) : null}
        </footer>
      </ResidentThemeRoot>
    );
  }

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <a href="#td-notify-form" className="td-skip-link">
        Skip to the subscribe form
      </a>
      <TownHeader embed={embed} />

      <main className="td-intake__main">
        <h1 className="td-intake__title">Get city updates.</h1>
        <p className="td-header__subcopy">
          {config.townName} sends occasional updates. Choose how you'd like to hear from us, then
          pick which updates you want to receive.
        </p>

        {submitState === "error" && submitErrorMsg ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>{submitErrorMsg}</p>
          </div>
        ) : null}

        <form
          id="td-notify-form"
          className="td-form"
          noValidate
          onSubmit={onSubmit}
          aria-busy={submitState === "submitting"}
        >
          <DeliveryMethodField idPrefix="notify" value={deliveryMethod} onChange={setDeliveryMethod} />

          <Field
            id={ID.email}
            label="Email"
            required={needsEmail}
            optionalHint={!needsEmail}
            error={emailError ?? undefined}
            helper={
              needsEmail ? (
                <>
                  We'll only use your email to send you these updates.{" "}
                  <strong>We never sell or share your information.</strong> You can{" "}
                  <strong>unsubscribe anytime</strong> — every email has a one-click link, no login
                  needed.
                </>
              ) : (
                "Add an email if you'd also like updates there — mailed updates alone don't need one."
              )
            }
          >
            {(aria) => (
              <input
                {...aria}
                className="td-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
              />
            )}
          </Field>

          <Field
            id={ID.name}
            label="Name"
            required={needsAddress}
            optionalHint={!needsAddress}
            error={nameError ?? undefined}
            helper={
              needsAddress
                ? "We'll address your mailed notices to this name."
                : "Lets staff address you by name when they follow up."
            }
          >
            {(aria) => (
              <input
                {...aria}
                className="td-input"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(null);
                }}
              />
            )}
          </Field>

          {needsAddress ? (
            <MailingAddressFields
              idPrefix="notify"
              value={mailingAddress}
              onChange={(next) => {
                setMailingAddress(next);
                if (Object.keys(addressErrors).length) setAddressErrors({});
              }}
              errors={addressErrors}
            />
          ) : null}

          {channels.length ? (
            <fieldset className="td-fieldset">
              <legend className="td-fieldset__legend" id={ID.channelsLegend}>
                What updates do you want?
              </legend>
              <p className="td-field__helper">
                Leave everything unchecked to get all city updates, or pick just the ones you care
                about.
              </p>
              <div className="td-channel-cards">
                {channels.map((c) => {
                  const checked = selectedChannels.has(c.id);
                  const inputId = `field-notify-channel-${c.id}`;
                  return (
                    <label
                      key={c.id}
                      htmlFor={inputId}
                      className={`td-channel-card${checked ? " is-selected" : ""}`}
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleChannel(c.id)}
                      />
                      <span className="td-channel-card__label">{c.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          {needsEmail ? (
            <p className="td-notify-doubleoptin">
              After you subscribe, we'll send a confirmation email — click the link in it to start
              receiving updates. This confirms it's really you, and keeps this list clean for
              everyone on it.
            </p>
          ) : null}

          <CaptchaField
            ref={captcha.containerRef}
            id={ID.captcha}
            status={captcha.status}
            error={captcha.error ?? undefined}
            passed={captcha.passed}
            onChange={captcha.onChange}
            onRetry={captcha.retry}
          />

          <div className="td-submit">
            <Button
              type="submit"
              fullWidth
              disabled={busy}
              aria-disabled={requiredMissing || busy}
            >
              {busy ? (
                <>
                  <SpinnerIcon width={18} height={18} />
                  Subscribing…
                </>
              ) : (
                "Subscribe"
              )}
            </Button>
          </div>
        </form>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        {config.privacyUrl ? (
          <a href={config.privacyUrl} rel="noopener">
            Privacy
          </a>
        ) : null}
      </footer>
    </ResidentThemeRoot>
  );
}

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}
